import { useState, useEffect, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import HomePage from "@/components/HomePage";
import AdminProtection from "@/components/AdminProtection";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { useAuth } from "@/contexts/AuthContext";

// Secondary tabs load their own code only when opened, keeping the homepage light.
const BlogPage = lazyWithRetry(() => import("@/components/BlogPage"));
const ChartsPage = lazyWithRetry(() => import("@/components/ChartsPage"));
const EconomicCalendarPage = lazyWithRetry(() => import("@/components/EconomicCalendarPage"));
const MentoringPage = lazyWithRetry(() => import("@/components/MentoringPage"));
const EducationContent = lazyWithRetry(() => import("@/components/EducationContent"));
const AdminPage = lazyWithRetry(() => import("@/components/AdminPage"));

const TabFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite">
    <div className="h-8 w-8 rounded-full border-2 border-muted border-t-trading-primary animate-spin" />
    <span className="sr-only">Loading</span>
  </div>
);

const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize activeTab based on URL parameter
    return searchParams.get("tab") || "home";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const { isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setActiveTab("blog");
  };

  const handleTabChange = (tab: string) => {
    if (tab === "calculators") {
      navigate("/calculators");
    } else if (tab === "journal") {
      navigate("/trading-journal");
    } else if (tab === "screener") {
      navigate("/screener");
    } else if (tab === "psychology") {
      navigate("/psychology");
    } else if (tab === "prop-firms") {
      navigate("/prop-firms");
    } else {
      setActiveTab(tab);
      // Update URL parameter
      if (tab === "home") {
        setSearchParams({});
      } else {
        setSearchParams({ tab });
      }
    }
  };

  // Listen for tab change events from footer on other pages
  useEffect(() => {
    const handleTabChangeEvent = (event: CustomEvent) => {
      const tab = event.detail;
      if (tab === "calculators") {
        navigate("/calculators");
      } else if (tab === "journal") {
        navigate("/trading-journal");
      } else if (tab === "screener") {
        navigate("/screener");
      } else if (tab === "psychology") {
        navigate("/psychology");
      } else if (tab === "prop-firms") {
        navigate("/prop-firms");
      } else if (tab === "education") {
        setActiveTab(tab);
        setSearchParams({ tab });
      } else {
        setActiveTab(tab);
        // Update URL parameter
        if (tab === "home") {
          setSearchParams({});
        } else {
          setSearchParams({ tab });
        }
      }
    };

    window.addEventListener('tabChange', handleTabChangeEvent as EventListener);
    return () => {
      window.removeEventListener('tabChange', handleTabChangeEvent as EventListener);
    };
  }, [navigate, setSearchParams]);

  // Listen for URL parameter changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    } else if (!tab && activeTab !== "home") {
      setActiveTab("home");
    }
  }, [searchParams, activeTab]);

  const renderContent = () => {
    switch (activeTab) {
      case "home":
        return <HomePage />;
      case "blog":
        return <BlogPage searchQuery={searchQuery} />;
      case "charts":
        return <ChartsPage />;
      case "calendar":
        return <EconomicCalendarPage />;
      case "mentoring":
        return <MentoringPage />;
      case "education":
        return <EducationContent />;
      case "admin":
        if (isLoading && !isAdmin) {
          return (
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-trading-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading admin...</p>
              </div>
            </div>
          );
        }

        return (
          <AdminProtection>
            <AdminPage />
          </AdminProtection>
        );
      default:
        return <HomePage />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1">
        <Suspense fallback={<TabFallback />}>{renderContent()}</Suspense>
      </main>
    </div>
  );
};

export default Index;
